// ─────────────────────────────────────────────────────────
// expressions.ts — Typed data structures for Saxa avatar
// Source: saxa-avatar-v2.jsx (data-only, no React components)
// ─────────────────────────────────────────────────────────

// ── Shared / utility types ──────────────────────────────

/** Elliptical highlight (large shine) */
export interface EllipseShine {
  dx: number;
  dy: number;
  rx: number;
  ry: number;
}

/** Circular highlight (small shine) */
export interface CircleShine {
  dx: number;
  dy: number;
  r: number;
}

export type Shine = EllipseShine | CircleShine;

// ── Anime expression types ──────────────────────────────

export interface AnimeColorSet {
  sclera: string;
  iris: string;
  accent: string;
}

export interface AnimeColors {
  dark: AnimeColorSet;
  light: AnimeColorSet;
}

export interface AnimeEye {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  irisRx: number;
  irisRy: number;
  irisOy: number;
  pupilR: number;
  pupilOy: number;
  shine: [EllipseShine, CircleShine];
  lidTop: number;
  lidBot: number;
  lashOut: boolean;
  heart?: boolean;
}

export interface AnimeBrowSide {
  d: string;
  w: number;
}

export interface AnimeBrow {
  left: AnimeBrowSide;
  right: AnimeBrowSide;
}

export interface AnimeExpression {
  label: string;
  colors: AnimeColors;
  left: AnimeEye;
  right: AnimeEye;
  brow: AnimeBrow;
}

export type AnimeExpressionName =
  | "neutral"
  | "happy"
  | "suspicious"
  | "surprised"
  | "sad"
  | "wink"
  | "focused"
  | "love"
  | "idle";

export type AnimeExpressionMap = Record<AnimeExpressionName, AnimeExpression>;

// ── Plain expression types ──────────────────────────────

export interface PlainSparkle {
  dx: number;
  dy: number;
  s: number;
}

export interface PlainBigEye {
  type: "big";
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  lidTop: number;
  sparkles: [PlainSparkle, PlainSparkle];
}

export interface PlainArcEye {
  type: "arc";
  d: string;
}

export type PlainEye = PlainBigEye | PlainArcEye;

export interface PlainStrokeMouth {
  type: "stroke";
  d: string;
  sw: number;
}

export interface PlainFillMouth {
  type: "fill";
  d: string;
}

export interface PlainRingMouth {
  type: "ring";
  cx: number;
  cy: number;
  r: number;
}

export type PlainMouth = PlainStrokeMouth | PlainFillMouth | PlainRingMouth;

export interface PlainExpression {
  label: string;
  leftEye: PlainEye;
  rightEye: PlainEye;
  mouth: PlainMouth;
}

export type PlainExpressionName =
  | "neutral"
  | "happy"
  | "grin"
  | "thinking"
  | "surprised"
  | "sad"
  | "wink"
  | "idle";

export type PlainExpressionMap = Record<PlainExpressionName, PlainExpression>;

// ── Rune types ──────────────────────────────────────────

export interface RuneSet {
  glyphs: [string, string, string, string, string];
  label: string;
}

export type RuneSetName =
  | "thinking"
  | "processing"
  | "responding"
  | "greeting"
  | "error"
  | "idle";

export type RuneSetMap = Record<RuneSetName, RuneSet>;

// ── AI expression types ──────────────────────────────────

export interface AiRing {
  rScale: number;
  w: number;
  opacity: number;
  rot: number;
  dur: number;
}

export interface AiExpression {
  label: string;
  coreScale: number;
  rings: AiRing[];
  rayCount: number;
  showNodes: boolean;
  showStreams: boolean;
  streamIn?: boolean;
  activeRays?: number[];
  pulse: number;
}

export type AiExpressionName =
  | "idle"
  | "thinking"
  | "processing"
  | "responding"
  | "analyzing"
  | "creating";

export type AiExpressionMap = Record<AiExpressionName, AiExpression>;

// ── Mode config types ───────────────────────────────────

export interface ModeConfigEntry<TExpr> {
  label: string;
  defaultExpr: string;
  expressions: TExpr;
}

export interface ModeConfig {
  ai: ModeConfigEntry<AiExpressionMap>;
  plain: ModeConfigEntry<PlainExpressionMap>;
  anime: ModeConfigEntry<AnimeExpressionMap>;
  runes: ModeConfigEntry<RuneSetMap>;
  // manga is handled separately via './manga-paths' for code-splitting
}

export type ModeName = keyof ModeConfig;

// ─────────────────────────────────────────────────────────
// Octagon geometry
// ─────────────────────────────────────────────────────────

export const octagonPath = (
  cx: number,
  cy: number,
  r: number,
  rotDeg: number = 0,
): string => {
  const rot = (rotDeg * Math.PI) / 180;
  const pts = Array.from({ length: 8 }, (_, i) => {
    const a = rot + (Math.PI * 2 * i) / 8 - Math.PI / 8;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  return (
    pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]},${p[1]}`)
      .join(" ") + " Z"
  );
};

// ─────────────────────────────────────────────────────────
// Sparkle path (solid 4-pointed AI star)
// ─────────────────────────────────────────────────────────

export const sparklePath = (cx: number, cy: number, s: number): string => {
  const a = s;
  const b = s * 0.25;
  return `M ${cx},${cy - a} L ${cx + b},${cy - b} L ${cx + a},${cy} L ${cx + b},${cy + b} L ${cx},${cy + a} L ${cx - b},${cy + b} L ${cx - a},${cy} L ${cx - b},${cy - b} Z`;
};

// ─────────────────────────────────────────────────────────
// ANIME_EXPR — 8 expressions (eyes-only, larger scale)
// ─────────────────────────────────────────────────────────

export const ANIME_EXPR: AnimeExpressionMap = {
  neutral: {
    label: "Neutral",
    colors: {
      dark: { sclera: "#60A5FA", iris: "#1E3A8A", accent: "#3B82F6" },
      light: { sclera: "#DBEAFE", iris: "#1a1a2e", accent: "#4a4a6a" },
    },
    left: {
      cx: -18, cy: 2, rx: 14, ry: 16,
      irisRx: 10, irisRy: 12, irisOy: 1,
      pupilR: 5, pupilOy: 1,
      shine: [{ dx: -4, dy: -5, rx: 3.5, ry: 4 }, { dx: 3.5, dy: -2, r: 1.5 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
    },
    right: {
      cx: 18, cy: 2, rx: 14, ry: 16,
      irisRx: 10, irisRy: 12, irisOy: 1,
      pupilR: 5, pupilOy: 1,
      shine: [{ dx: -4, dy: -5, rx: 3.5, ry: 4 }, { dx: 3.5, dy: -2, r: 1.5 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
    },
    brow: {
      left: { d: "M -29,-16 Q -17,-21 -7,-18", w: 2.8 },
      right: { d: "M 7,-18 Q 17,-21 29,-16", w: 2.8 },
    },
  },
  happy: {
    label: "Happy",
    colors: {
      dark: { sclera: "#C4B5FD", iris: "#4C1D95", accent: "#8B5CF6" },
      light: { sclera: "#EDE9FE", iris: "#5B21B6", accent: "#7C3AED" },
    },
    left: {
      cx: -18, cy: 2, rx: 15, ry: 18,
      irisRx: 11, irisRy: 13, irisOy: -1,
      pupilR: 5, pupilOy: -1,
      shine: [{ dx: -4, dy: -6, rx: 4, ry: 5 }, { dx: 4, dy: -3, r: 1.8 }],
      lidTop: 0, lidBot: 0.35,
      lashOut: true,
    },
    right: {
      cx: 18, cy: 2, rx: 15, ry: 18,
      irisRx: 11, irisRy: 13, irisOy: -1,
      pupilR: 5, pupilOy: -1,
      shine: [{ dx: -4, dy: -6, rx: 4, ry: 5 }, { dx: 4, dy: -3, r: 1.8 }],
      lidTop: 0, lidBot: 0.35,
      lashOut: true,
    },
    brow: {
      left: { d: "M -29,-17 Q -17,-20 -7,-18", w: 2.5 },
      right: { d: "M 7,-18 Q 17,-20 29,-17", w: 2.5 },
    },
  },
  suspicious: {
    label: "Suspicious",
    colors: {
      dark: { sclera: "#3B82F6", iris: "#1E3A8A", accent: "#2563EB" },
      light: { sclera: "#BFDBFE", iris: "#1E3A5E", accent: "#3B6EB0" },
    },
    left: {
      cx: -18, cy: 2, rx: 14, ry: 11,
      irisRx: 8, irisRy: 8, irisOy: 2,
      pupilR: 3.5, pupilOy: 2,
      shine: [{ dx: -3, dy: -3, rx: 2.5, ry: 2.5 }, { dx: 2.5, dy: -1, r: 1 }],
      lidTop: 0.3, lidBot: 0,
      lashOut: true,
    },
    right: {
      cx: 18, cy: 2, rx: 14, ry: 11,
      irisRx: 8, irisRy: 8, irisOy: 2,
      pupilR: 3.5, pupilOy: 2,
      shine: [{ dx: -3, dy: -3, rx: 2.5, ry: 2.5 }, { dx: 2.5, dy: -1, r: 1 }],
      lidTop: 0.3, lidBot: 0,
      lashOut: true,
    },
    brow: {
      left: { d: "M -29,-14 Q -17,-13 -7,-11", w: 3 },
      right: { d: "M 7,-8 Q 17,-12 29,-16", w: 3 },
    },
  },
  surprised: {
    label: "Surprised",
    colors: {
      dark: { sclera: "#67E8F9", iris: "#164E63", accent: "#06B6D4" },
      light: { sclera: "#CFFAFE", iris: "#155E75", accent: "#0891B2" },
    },
    left: {
      cx: -18, cy: 2, rx: 16, ry: 20,
      irisRx: 9, irisRy: 11, irisOy: 0,
      pupilR: 4, pupilOy: 0,
      shine: [{ dx: -4.5, dy: -6, rx: 4, ry: 5 }, { dx: 4, dy: -2, r: 1.8 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
    },
    right: {
      cx: 18, cy: 2, rx: 16, ry: 20,
      irisRx: 9, irisRy: 11, irisOy: 0,
      pupilR: 4, pupilOy: 0,
      shine: [{ dx: -4.5, dy: -6, rx: 4, ry: 5 }, { dx: 4, dy: -2, r: 1.8 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
    },
    brow: {
      left: { d: "M -29,-21 Q -17,-28 -7,-23", w: 2.8 },
      right: { d: "M 7,-23 Q 17,-28 29,-21", w: 2.8 },
    },
  },
  sad: {
    label: "Sad",
    colors: {
      dark: { sclera: "#C4B5FD", iris: "#3B0764", accent: "#7C3AED" },
      light: { sclera: "#EDE9FE", iris: "#4C1D95", accent: "#6D28D9" },
    },
    left: {
      cx: -18, cy: 4, rx: 14, ry: 14,
      irisRx: 10, irisRy: 11, irisOy: 3,
      pupilR: 5, pupilOy: 3,
      shine: [{ dx: -4, dy: -3, rx: 3.5, ry: 3.5 }, { dx: 3, dy: 0, r: 1.3 }],
      lidTop: 0.18, lidBot: 0,
      lashOut: false,
    },
    right: {
      cx: 18, cy: 4, rx: 14, ry: 14,
      irisRx: 10, irisRy: 11, irisOy: 3,
      pupilR: 5, pupilOy: 3,
      shine: [{ dx: -4, dy: -3, rx: 3.5, ry: 3.5 }, { dx: 3, dy: 0, r: 1.3 }],
      lidTop: 0.18, lidBot: 0,
      lashOut: false,
    },
    brow: {
      left: { d: "M -29,-13 Q -17,-17 -7,-16", w: 2.5 },
      right: { d: "M 7,-16 Q 17,-17 29,-13", w: 2.5 },
    },
  },
  wink: {
    label: "Wink",
    colors: {
      dark: { sclera: "#F9A8D4", iris: "#831843", accent: "#EC4899" },
      light: { sclera: "#FCE7F3", iris: "#9D174D", accent: "#DB2777" },
    },
    left: {
      cx: -18, cy: 2, rx: 14, ry: 16,
      irisRx: 10, irisRy: 12, irisOy: 1,
      pupilR: 5, pupilOy: 1,
      shine: [{ dx: -4, dy: -5, rx: 3.5, ry: 4 }, { dx: 3.5, dy: -2, r: 1.5 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
    },
    right: {
      cx: 18, cy: 2, rx: 14, ry: 16,
      irisRx: 10, irisRy: 12, irisOy: 1,
      pupilR: 5, pupilOy: 1,
      shine: [{ dx: -4, dy: -5, rx: 3.5, ry: 4 }, { dx: 3.5, dy: -2, r: 1.5 }],
      lidTop: 0.85, lidBot: 0,
      lashOut: true,
    },
    brow: {
      left: { d: "M -29,-16 Q -17,-21 -7,-18", w: 2.8 },
      right: { d: "M 7,-14 Q 17,-18 29,-16", w: 2.5 },
    },
  },
  focused: {
    label: "Focused",
    colors: {
      dark: { sclera: "#6EE7B7", iris: "#064E3B", accent: "#10B981" },
      light: { sclera: "#D1FAE5", iris: "#065F46", accent: "#059669" },
    },
    left: {
      cx: -18, cy: 2, rx: 13, ry: 12,
      irisRx: 8, irisRy: 9, irisOy: 0,
      pupilR: 3.5, pupilOy: 0,
      shine: [{ dx: -3, dy: -3.5, rx: 2.5, ry: 3 }, { dx: 2.5, dy: -1, r: 1.1 }],
      lidTop: 0.2, lidBot: 0,
      lashOut: true,
    },
    right: {
      cx: 18, cy: 2, rx: 13, ry: 12,
      irisRx: 8, irisRy: 9, irisOy: 0,
      pupilR: 3.5, pupilOy: 0,
      shine: [{ dx: -3, dy: -3.5, rx: 2.5, ry: 3 }, { dx: 2.5, dy: -1, r: 1.1 }],
      lidTop: 0.2, lidBot: 0,
      lashOut: true,
    },
    brow: {
      left: { d: "M -29,-14 Q -17,-12 -7,-9", w: 3 },
      right: { d: "M 7,-9 Q 17,-12 29,-14", w: 3 },
    },
  },
  love: {
    label: "Love",
    colors: {
      dark: { sclera: "#FDA4AF", iris: "#881337", accent: "#F43F5E" },
      light: { sclera: "#FFE4E6", iris: "#9F1239", accent: "#E11D48" },
    },
    left: {
      cx: -18, cy: 2, rx: 15, ry: 17,
      irisRx: 10, irisRy: 12, irisOy: 0,
      pupilR: 5, pupilOy: 0,
      shine: [{ dx: -4, dy: -5, rx: 3.5, ry: 4 }, { dx: 3.5, dy: -2, r: 1.5 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
      heart: true,
    },
    right: {
      cx: 18, cy: 2, rx: 15, ry: 17,
      irisRx: 10, irisRy: 12, irisOy: 0,
      pupilR: 5, pupilOy: 0,
      shine: [{ dx: -4, dy: -5, rx: 3.5, ry: 4 }, { dx: 3.5, dy: -2, r: 1.5 }],
      lidTop: 0, lidBot: 0,
      lashOut: true,
      heart: true,
    },
    brow: {
      left: { d: "M -29,-17 Q -17,-23 -7,-19", w: 2.2 },
      right: { d: "M 7,-19 Q 17,-23 29,-17", w: 2.2 },
    },
  },
  idle: {
    label: "Idle",
    colors: {
      dark: { sclera: "#7BA3CC", iris: "#2D4A6E", accent: "#5A8AB5" },
      light: { sclera: "#E2E8F0", iris: "#475569", accent: "#94A3B8" },
    },
    left: {
      cx: -18, cy: 3, rx: 13, ry: 13,
      irisRx: 9, irisRy: 10, irisOy: 2,
      pupilR: 4, pupilOy: 2,
      shine: [{ dx: -3, dy: -4, rx: 2.5, ry: 3 }, { dx: 2.5, dy: -1, r: 1.2 }],
      lidTop: 0.45, lidBot: 0.1,
      lashOut: false,
    },
    right: {
      cx: 18, cy: 3, rx: 13, ry: 13,
      irisRx: 9, irisRy: 10, irisOy: 2,
      pupilR: 4, pupilOy: 2,
      shine: [{ dx: -3, dy: -4, rx: 2.5, ry: 3 }, { dx: 2.5, dy: -1, r: 1.2 }],
      lidTop: 0.45, lidBot: 0.1,
      lashOut: false,
    },
    brow: {
      left: { d: "M -28,-13 Q -17,-15 -8,-14", w: 2.2 },
      right: { d: "M 8,-14 Q 17,-15 28,-13", w: 2.2 },
    },
  },
};

// ─────────────────────────────────────────────────────────
// PLAIN_EXPR — 8 expressions (emoji-style, eyes + mouth)
// ─────────────────────────────────────────────────────────

export const PLAIN_EXPR: PlainExpressionMap = {
  neutral: {
    label: "Neutral",
    leftEye: {
      type: "big", cx: -18, cy: 0, rx: 8, ry: 10, lidTop: 0,
      sparkles: [{ dx: -2, dy: -3, s: 6 }, { dx: 3, dy: 3, s: 3 }],
    },
    rightEye: {
      type: "big", cx: 18, cy: 0, rx: 8, ry: 10, lidTop: 0,
      sparkles: [{ dx: -2, dy: -3, s: 6 }, { dx: 3, dy: 3, s: 3 }],
    },
    mouth: { type: "stroke", d: "M -10,18 Q 0,21 10,18", sw: 3 },
  },
  happy: {
    label: "Happy",
    leftEye: {
      type: "big", cx: -18, cy: 0, rx: 8, ry: 8, lidTop: 0.15,
      sparkles: [{ dx: -2, dy: -2, s: 5 }, { dx: 3, dy: 2, s: 2.5 }],
    },
    rightEye: {
      type: "big", cx: 18, cy: 0, rx: 8, ry: 8, lidTop: 0.15,
      sparkles: [{ dx: -2, dy: -2, s: 5 }, { dx: 3, dy: 2, s: 2.5 }],
    },
    mouth: { type: "stroke", d: "M -12,16 Q 0,26 12,16", sw: 3 },
  },
  grin: {
    label: "Grin",
    leftEye: { type: "arc", d: "M -26,0 Q -18,-10 -10,0" },
    rightEye: { type: "arc", d: "M 10,0 Q 18,-10 26,0" },
    mouth: { type: "fill", d: "M -13,14 Q 0,28 13,14 Z" },
  },
  thinking: {
    label: "Thinking",
    leftEye: {
      type: "big", cx: -18, cy: 0, rx: 8, ry: 10, lidTop: 0,
      sparkles: [{ dx: -2, dy: -3, s: 6 }, { dx: 3, dy: 3, s: 3 }],
    },
    rightEye: {
      type: "big", cx: 18, cy: 0, rx: 7, ry: 8, lidTop: 0.15,
      sparkles: [{ dx: -2, dy: -2, s: 5 }, { dx: 2, dy: 2, s: 2.5 }],
    },
    mouth: { type: "stroke", d: "M -2,20 Q 5,18 10,21", sw: 2.8 },
  },
  surprised: {
    label: "Surprised",
    leftEye: {
      type: "big", cx: -18, cy: 0, rx: 9, ry: 12, lidTop: 0,
      sparkles: [{ dx: -2, dy: -4, s: 7 }, { dx: 3, dy: 4, s: 3.5 }],
    },
    rightEye: {
      type: "big", cx: 18, cy: 0, rx: 9, ry: 12, lidTop: 0,
      sparkles: [{ dx: -2, dy: -4, s: 7 }, { dx: 3, dy: 4, s: 3.5 }],
    },
    mouth: { type: "ring", cx: 0, cy: 20, r: 5 },
  },
  sad: {
    label: "Sad",
    leftEye: {
      type: "big", cx: -18, cy: 1, rx: 8, ry: 9, lidTop: 0.1,
      sparkles: [{ dx: -2, dy: -3, s: 5.5 }, { dx: 3, dy: 2, s: 2.5 }],
    },
    rightEye: {
      type: "big", cx: 18, cy: 1, rx: 8, ry: 9, lidTop: 0.1,
      sparkles: [{ dx: -2, dy: -3, s: 5.5 }, { dx: 3, dy: 2, s: 2.5 }],
    },
    mouth: { type: "stroke", d: "M -10,22 Q 0,16 10,22", sw: 3 },
  },
  wink: {
    label: "Wink",
    leftEye: {
      type: "big", cx: -18, cy: 0, rx: 8, ry: 10, lidTop: 0,
      sparkles: [{ dx: -2, dy: -3, s: 6 }, { dx: 3, dy: 3, s: 3 }],
    },
    rightEye: { type: "arc", d: "M 10,0 Q 18,6 26,0" },
    mouth: { type: "stroke", d: "M -12,16 Q 0,24 12,16", sw: 3 },
  },
  idle: {
    label: "Idle",
    leftEye: {
      type: "big", cx: -18, cy: 1, rx: 7, ry: 8, lidTop: 0.35,
      sparkles: [{ dx: -2, dy: -2, s: 4 }, { dx: 2, dy: 2, s: 2 }],
    },
    rightEye: {
      type: "big", cx: 18, cy: 1, rx: 7, ry: 8, lidTop: 0.35,
      sparkles: [{ dx: -2, dy: -2, s: 4 }, { dx: 2, dy: 2, s: 2 }],
    },
    mouth: { type: "stroke", d: "M -8,19 Q 0,20 8,19", sw: 2.5 },
  },
};

// ─────────────────────────────────────────────────────────
// RUNE_SETS — 6 rune glyph sets
// ─────────────────────────────────────────────────────────

export const RUNE_SETS: RuneSetMap = {
  thinking:   { glyphs: ["\u16B1", "\u16A2", "\u16BE", "\u16C1", "\u16B2"], label: "Thinking" },
  processing: { glyphs: ["\u16C3", "\u16C7", "\u16C8", "\u16C9", "\u16CA"], label: "Processing" },
  responding: { glyphs: ["\u16A0", "\u16A8", "\u16DA", "\u16DE", "\u16DF"], label: "Responding" },
  greeting:   { glyphs: ["\u16BA", "\u16A8", "\u16C1", "\u16DA", "\u16EB"], label: "Greeting" },
  error:      { glyphs: ["\u16CF", "\u16B1", "\u16C1", "\u16EB", "\u16EB"], label: "Error" },
  idle:       { glyphs: ["\u16EB", "\u16EB", "\u16EB", "\u16EB", "\u16EB"], label: "Idle" },
};

// ─────────────────────────────────────────────────────────
// AI_EXPR — 6 expressions (geometric core + rings)
// ─────────────────────────────────────────────────────────

export const AI_EXPR: AiExpressionMap = {
  idle: {
    label: "Idle",
    coreScale: 0.14,
    rings: [
      { rScale: 0.38, w: 0.9, opacity: 0.4, rot: 11.25, dur: 0 },
    ],
    rayCount: 0,
    showNodes: false,
    showStreams: false,
    pulse: 5,
  },
  thinking: {
    label: "Thinking",
    coreScale: 0.16,
    rings: [
      { rScale: 0.3, w: 1, opacity: 0.5, rot: 11.25, dur: 55 },
      { rScale: 0.65, w: 0.65, opacity: 0.3, rot: 0, dur: -80 },
    ],
    rayCount: 4,
    showNodes: true,
    showStreams: false,
    pulse: 3.5,
  },
  processing: {
    label: "Processing",
    coreScale: 0.17,
    rings: [
      { rScale: 0.26, w: 1, opacity: 0.5, rot: 11.25, dur: 45 },
      { rScale: 0.54, w: 0.7, opacity: 0.35, rot: 0, dur: -60 },
      { rScale: 0.84, w: 0.45, opacity: 0.2, rot: 11.25, dur: 40 },
    ],
    rayCount: 8,
    showNodes: true,
    showStreams: true,
    streamIn: true,
    pulse: 2.5,
  },
  responding: {
    label: "Responding",
    coreScale: 0.22,
    rings: [
      { rScale: 0.3, w: 1.2, opacity: 0.6, rot: 11.25, dur: 40 },
      { rScale: 0.58, w: 0.8, opacity: 0.42, rot: 0, dur: -55 },
      { rScale: 0.85, w: 0.5, opacity: 0.28, rot: 11.25, dur: 30 },
    ],
    rayCount: 8,
    showNodes: true,
    showStreams: true,
    streamIn: false,
    pulse: 1.8,
  },
  analyzing: {
    label: "Analyzing",
    coreScale: 0.15,
    rings: [
      { rScale: 0.28, w: 1, opacity: 0.5, rot: 11.25, dur: -40 },
      { rScale: 0.56, w: 0.7, opacity: 0.35, rot: 0, dur: 50 },
      { rScale: 0.84, w: 0.45, opacity: 0.2, rot: 11.25, dur: -65 },
    ],
    rayCount: 8,
    showNodes: true,
    showStreams: true,
    streamIn: true,
    activeRays: [0, 2, 5, 7],
    pulse: 3,
  },
  creating: {
    label: "Creating",
    coreScale: 0.2,
    rings: [
      { rScale: 0.22, w: 1.2, opacity: 0.65, rot: 11.25, dur: 50 },
      { rScale: 0.42, w: 0.85, opacity: 0.48, rot: 0, dur: -38 },
      { rScale: 0.64, w: 0.6, opacity: 0.32, rot: 11.25, dur: 55 },
      { rScale: 0.86, w: 0.4, opacity: 0.18, rot: 0, dur: -32 },
    ],
    rayCount: 8,
    showNodes: true,
    showStreams: true,
    streamIn: false,
    pulse: 2.5,
  },
};

// ─────────────────────────────────────────────────────────
// MODE_CONFIG — avatar rendering modes
// Manga expressions are imported separately from './manga-paths'
// for code-splitting. Only anime, plain, and runes are included here.
// ─────────────────────────────────────────────────────────

export const MODE_CONFIG: ModeConfig = {
  ai:    { label: "AI Core",     defaultExpr: "idle",    expressions: AI_EXPR },
  plain: { label: "Plain Emoji", defaultExpr: "neutral", expressions: PLAIN_EXPR },
  anime: { label: "Anime Eyes",  defaultExpr: "neutral", expressions: ANIME_EXPR },
  runes: { label: "Runes",       defaultExpr: "idle",    expressions: RUNE_SETS },
};
